// Weight as a trend, never a verdict: the seven-day average and how it has moved over four weeks.
export type WeighIn = { d: string; kg: number; fat?: number | null };
const day = (iso: string) => iso.slice(0, 10);

/** One reading per day (the last of the day), oldest first. */
export function daily(w: WeighIn[]): { d: string; kg: number }[] {
  const m = new Map<string, number>();
  for (const x of [...w].sort((a, b) => (a.d < b.d ? -1 : 1))) m.set(day(x.d), x.kg);
  return [...m].map(([d, kg]) => ({ d, kg }));
}

/** Average of the days within the seven ending on `end` (ISO date); null with fewer than three readings. */
export function avg7(days: { d: string; kg: number }[], end: string): number | null {
  const start = new Date(Date.parse(end + "T12:00:00Z") - 6 * 86400000).toISOString().slice(0, 10);
  const xs = days.filter((x) => x.d >= start && x.d <= end).map((x) => x.kg);
  return xs.length >= 3 ? Math.round((xs.reduce((a, b) => a + b, 0) / xs.length) * 10) / 10 : null;
}

/** Kyle's scales (FITINDEX) read in stones and pounds, so Musette does too unless he picks kg. */
export type WUnit = "st" | "kg";
export function fmtW(kg: number, unit: WUnit = "st"): string {
  if (unit === "kg") return `${kg.toFixed(1)} kg`;
  const lb = kg / 0.45359237;
  let st = Math.floor(lb / 14), rest = Math.round((lb - st * 14) * 10) / 10;
  if (rest >= 14) { st += 1; rest -= 14; }
  return `${st} st ${rest.toFixed(1)} lb`;
}
/** A change in weight, in the same unit: pounds when the unit is stones. */
export function fmtDelta(kg: number, unit: WUnit = "st"): string {
  return unit === "kg" ? `${Math.abs(kg).toFixed(1)} kg` : `${Math.abs(kg / 0.45359237).toFixed(1)} lb`;
}
/** Read what he types: "14 11.8", "14st 11.8", "14:11.8" as stones and pounds; a plain number in his unit. */
export function parseW(s: string, unit: WUnit = "st"): number | null {
  const t = s.trim().toLowerCase().replace(",", ".");
  const m = /^(\d{1,2})\s*(?:st|:|\s)\s*(\d{1,2}(?:\.\d)?)\s*(?:lb)?$/.exec(t);
  if (m) return (Number(m[1]) * 14 + Number(m[2])) * 0.45359237;
  const n = Number(t.replace(/[^\d.]/g, ""));
  if (!Number.isFinite(n) || !n) return null;
  if (/lb/.test(t)) return n * 0.45359237;
  return unit === "kg" || n > 40 ? n : n * 6.35029318;
}
